import { create } from 'zustand';
import i18n from 'i18n';
import { AccountUtils } from '@shared/utils';
import { type AccountEntity, AccountsApi, ApiUtils, type GetAccountsCardsAccessResponse, ResourceApi } from '@shared/api';
import type { BaseReturn } from '@shared/types';

type GroupedAccounts = { [accountId: string]: GetAccountsCardsAccessResponse[] };

interface UseAccountsStore {
  loading: boolean;
  data: AccountEntity[];
  error?: string | null;
  getAccounts: () => Promise<BaseReturn<AccountEntity[]>>;
  clear: () => void;
}

export const useAccountsStore = create<UseAccountsStore>((set) => ({
  loading: true,
  data: [],
  error: null,

  getAccounts: async () => {
    try {
      set({ loading: true, data: [], error: null });
      const accountsCardsAccessResult = await AccountsApi.getAccountsCardsAccess();
      let result: AccountEntity[] | null = null;
      if (accountsCardsAccessResult?.length) {
        const productImages: Record<string, { description: string; data: Blob }> = {};

        if (accountsCardsAccessResult[0].itemType === 'Account') {
          const products = [...new Set(accountsCardsAccessResult.map((account) => account.productCode))];

          await Promise.all(
            products.map(async (product) => {
              const [info, data] = await Promise.all([ResourceApi.getProductImageInfo(product), ResourceApi.getProductImage(product)]);
              productImages[product] = { description: info[0].description, data };
            }),
          );

          const accounts = await Promise.all(
            accountsCardsAccessResult.map(async (item) => {
              const [cards, limits] = await Promise.all([
                AccountsApi.getAccountCards(item.accountId),
                AccountsApi.getAccountLimitsV2(item.accountId),
              ]);

              const cardsWithPhones = await Promise.all(
                cards.map(async (card) => {
                  const mobile = await AccountsApi.getCardPhone(card.cardId);
                  return { ...card, mobile };
                }),
              );

              return {
                ...item,
                cards: cardsWithPhones,
                limits,
                image: productImages[item.productCode],
              };
            }),
          );

          result = accounts.map(AccountUtils.decodeAccount);
        } else if (accountsCardsAccessResult[0].itemType === 'Card') {
          const products = [...new Set(accountsCardsAccessResult.map((account) => account.productCode))];
          await Promise.all(
            products.map(async (product) => {
              const [info, data] = await Promise.all([ResourceApi.getProductImageInfo(product), ResourceApi.getProductImage(product)]);
              productImages[product] = { description: info[0].description, data };
            }),
          );

          const groupedAccounts = accountsCardsAccessResult.reduce((acc: GroupedAccounts, item) => {
            const key = item.accountId.toString();
            if (!acc[key]) acc[key] = [item];
            else acc[key].push(item);
            return acc;
          }, {} as GroupedAccounts);

          const accounts = await Promise.all(
            Object.keys(groupedAccounts).map(async (accountId) => {
              const accounts = groupedAccounts[accountId];
              const [cards, limits] = await Promise.all([
                Promise.all(accounts.map((account) => AccountsApi.getCardInfo(account.cardId))),
                AccountsApi.getCardLimits(accounts[0].cardId),
              ]);

              const cardsWithPhones = await Promise.all(
                cards[0].map(async (card) => {
                  const mobile = await AccountsApi.getCardPhone(card.cardId);
                  return { ...card, mobile };
                }),
              );

              return {
                accountId,
                productCode: accounts[0].productCode,
                productName: accounts[0].productName,
                displayName: accounts[0].displayName,
                currency: accounts[0].currency,
                claims: accounts[0].claims,
                cards: cardsWithPhones,
                limits,
                image: productImages[accounts[0].productCode],
              };
            }),
          );
          //@ts-ignore
          result = accounts.map(AccountUtils.decodeAccount);
        }
      }
      set({ loading: false, data: result || [] });
      return { loading: false, data: result || [] };
    } catch (e) {
      const error = i18n.t(`errors.api.${ApiUtils.getError(e)}`, ApiUtils.getError(e));
      set({ loading: false, data: [], error });
      return { loading: false, data: [], error };
    }
  },

  clear: () => set({ loading: false, data: [], error: null }),
}));
